import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { HighwayArea } from '@thive/fddm-types';

import { Accordion } from '@/modules/common/components/Accordion';
import { AccordionAddIconButton } from '@/modules/common/components/AccordionAddIconButton';
import { WorkspaceMode } from '@modules/common/types/general';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { JitRoutingPoints } from './JitRoutingPoints';
import { useAppStore } from '@/modules/app/store/useAppStore';

interface Props {
  selectedHighway: HighwayArea;
}

function JitRoutingPointPropertiesComponent({ selectedHighway }: Props) {
  const { t } = useTranslation('interface');
  const mode = useAppStore((state) => state.mode);
  const areas = useFloorplanDesignStore((state) => state.design.areas);
  const jitRoutingPoints = useFloorplanDesignStore(
    (state) => state.design.justInTimeRoutingConfigs,
  );
  const { id, highwayPoiSpecs, segments } = selectedHighway;
  const relatedJitRoutingPoints = useMemo(
    () => jitRoutingPoints.filter((config) => segments.map(s => s.id).includes(config.highwayAreaId)),
    [jitRoutingPoints, segments],
  );
  const [expanded, setExpanded] = useState(true);
  const [showDraft, setShowDraft] = useState(false);

  const handleAddJitRoutingPoint = useCallback(() => {
    setShowDraft(true);
    setExpanded(true);
  }, []);

  const handleDismissDraft = useCallback(() => {
    setShowDraft(false);
  }, []);

  const isAddDisabled =
    mode !== WorkspaceMode.EDITABLE || highwayPoiSpecs[0].roadType !== 'ONE_WAY' || showDraft;

  if (!id || !highwayPoiSpecs || segments.length > 1) return null;

  const showList = relatedJitRoutingPoints.length > 0 || showDraft;

  return (
    <Stack sx={{ px: 2, pt: 1 }} spacing={1}>
      <Accordion
        sx={{ px: 0 }}
        title={
          <Typography
            align='center'
            sx={{
              fontSize: '14px',
              fontWeight: 400,
            }}
          >
            {t('jit_routing_points.header', 'Just-In-Time points', {
              count: relatedJitRoutingPoints.length,
            })}
          </Typography>
        }
        expanded={expanded}
        onChange={setExpanded}
        afterIcon={
          <AccordionAddIconButton
            tooltip={t('interface:jit_routing_points.disabled.plus_icon')}
            disabled={isAddDisabled}
            onAdd={handleAddJitRoutingPoint}
            onOpen={() => setExpanded(true)}
          />
        }
      >
        {showList && (
          <JitRoutingPoints
            relatedJitRoutingPoints={relatedJitRoutingPoints}
            jitRoutingPoints={jitRoutingPoints}
            areas={areas}
            showDraft={showDraft}
            segmentId={segments[0].id}
            onDismissDraft={handleDismissDraft}
          />
        )}
      </Accordion>
    </Stack>
  );
}

export const JitRoutingPointProperties = memo(JitRoutingPointPropertiesComponent);
